import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../apiBase';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import './sms-management.css';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
}

const fmt = (n, cur = '₽') => `${Number(n ?? 0).toLocaleString('ru-RU', { maximumFractionDigits: 4 })} ${cur === 'RUB' ? '₽' : cur}`;

const emptyTariffDraft = () => ({
    region: '',
    pricePerSms: '',
    markupPercent: '45',
    costPerSms: '',
    package100Price: '',
    package500Price: '',
    package1000Price: '',
    package5000Price: '',
    minBalanceToSend: '1',
    currency: 'RUB',
    isActive: true,
});

function tariffToDraft(tar) {
    return {
        region: tar.region ?? '',
        pricePerSms: String(tar.pricePerSms ?? ''),
        markupPercent: String(tar.markupPercent ?? ''),
        costPerSms: tar.costPerSms != null ? String(tar.costPerSms) : '',
        package100Price: String(tar.package100Price ?? ''),
        package500Price: String(tar.package500Price ?? ''),
        package1000Price: String(tar.package1000Price ?? ''),
        package5000Price: String(tar.package5000Price ?? ''),
        minBalanceToSend: String(tar.minBalanceToSend ?? '1'),
        currency: tar.currency ?? 'RUB',
        isActive: tar.isActive !== false,
    };
}

function validateTariffDraft(draft, sourceTar) {
    const pricePerSms = Number(draft.pricePerSms);
    const package100Price = Number(draft.package100Price);
    const region = (draft.region || '').trim().toLowerCase();

    if (!region) return 'Укажите регион';
    if (!(pricePerSms > 0)) return 'Цена за SMS должна быть больше 0';

    const minPackage100 = pricePerSms * 100 * 0.5;
    if (!(package100Price >= minPackage100)) {
        return `Пакет 100 не может быть дешевле 50% от поштучной цены (мин. ${minPackage100.toFixed(2)})`;
    }

    const costFromDraft = draft.costPerSms !== '' && draft.costPerSms != null
        ? Number(draft.costPerSms)
        : null;
    const costFromSource = sourceTar?.costPerSms != null ? Number(sourceTar.costPerSms) : null;
    const costPerSms = costFromDraft ?? costFromSource;

    if (costPerSms != null && costPerSms > 0 && pricePerSms < costPerSms) {
        return 'Цена за SMS не может быть ниже себестоимости';
    }

    return null;
}

function draftToPayload(draft, sourceTar) {
    const costFromDraft = draft.costPerSms !== '' && draft.costPerSms != null
        ? Number(draft.costPerSms)
        : null;
    const costFromSource = sourceTar?.costPerSms != null ? Number(sourceTar.costPerSms) : null;

    return {
        pricePerSms: Number(draft.pricePerSms),
        markupPercent: Number(draft.markupPercent) || 0,
        costPerSms: costFromDraft ?? costFromSource ?? 0,
        package100Price: Number(draft.package100Price),
        package500Price: Number(draft.package500Price),
        package1000Price: Number(draft.package1000Price),
        package5000Price: Number(draft.package5000Price),
        minBalanceToSend: Number(draft.minBalanceToSend) || 1,
        currency: (draft.currency || 'RUB').trim().toUpperCase(),
        isActive: !!draft.isActive,
    };
}

function TariffEditInput({ value, onChange, type = 'text', style }) {
    return (
        <input
            className="sms-mgmt-input"
            type={type}
            value={value}
            onChange={onChange}
            style={{ width: '100%', minWidth: 56, margin: 0, ...style }}
        />
    );
}

export default function SmsManagementPage() {
    const { t } = useTranslation(['adminSms', 'common']);
    const [tab, setTab] = useState('providers');
    const [providers, setProviders] = useState([]);
    const [tariffs, setTariffs] = useState([]);
    const [balances, setBalances] = useState([]);
    const [revenue, setRevenue] = useState(null);
    const [msg, setMsg] = useState('');
    const [testPhone, setTestPhone] = useState('');
    const [adjustSchoolId, setAdjustSchoolId] = useState('');
    const [adjustAmount, setAdjustAmount] = useState('');
    const [adjustReason, setAdjustReason] = useState('');
    const [editingKey, setEditingKey] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const [savingTariff, setSavingTariff] = useState(false);

    useEffect(() => { loadTab(tab); }, [tab]);

    async function loadTab(name) {
        setMsg('');
        try {
            if (name === 'providers') {
                const res = await adminApi().get('/admin/sms/providers');
                setProviders(res.data || []);
            }
            if (name === 'tariffs') {
                const res = await adminApi().get('/admin/sms/tariffs');
                setTariffs(res.data || []);
                setEditingKey(null);
                setEditDraft(null);
            }
            if (name === 'balances') {
                const res = await adminApi().get('/admin/sms/schools-balance');
                setBalances(res.data || []);
            }
            if (name === 'revenue') {
                const res = await adminApi().get('/admin/sms/revenue');
                setRevenue(res.data);
            }
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    async function testProvider(id) {
        if (!testPhone.trim()) return;
        try {
            const res = await adminApi().post(`/admin/sms/providers/${id}/test`, { testPhone });
            setMsg(res.data?.success ? 'OK' : (res.data?.error || t('common:error')));
        } catch (e) {
            setMsg(e.response?.data?.error || t('common:error'));
        }
    }

    function startEditTariff(tar) {
        setEditingKey(tar.id);
        setEditDraft(tariffToDraft(tar));
        setMsg('');
    }

    function startNewTariff() {
        setEditingKey('new');
        setEditDraft(emptyTariffDraft());
        setMsg('');
    }

    function cancelEditTariff() {
        setEditingKey(null);
        setEditDraft(null);
    }

    function patchDraft(field, value) {
        setEditDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    }

    async function submitTariff() {
        if (!editDraft || savingTariff) return;

        const sourceTar = editingKey === 'new'
            ? null
            : tariffs.find((x) => x.id === editingKey);

        const validationError = validateTariffDraft(editDraft, sourceTar);
        if (validationError) {
            setMsg(validationError);
            return;
        }

        const region = (editDraft.region || '').trim().toLowerCase();
        const payload = draftToPayload(editDraft, sourceTar);

        setSavingTariff(true);
        setMsg('');
        try {
            await adminApi().put(`/admin/sms/tariffs/${encodeURIComponent(region)}`, payload);
            setMsg(t('common:done'));
            setEditingKey(null);
            setEditDraft(null);
            await loadTab('tariffs');
        } catch (e) {
            setMsg(e.response?.data?.message || e.response?.data?.error || t('common:error'));
        } finally {
            setSavingTariff(false);
        }
    }

    async function adjustBalance() {
        const schoolId = Number(adjustSchoolId);
        const smsAmount = Number(adjustAmount);
        if (!schoolId || !smsAmount) return;
        try {
            await adminApi().post(`/admin/sms/schools/${schoolId}/adjust`, {
                smsAmount,
                reason: adjustReason || t('adminSms:adjustBalance')
            });
            setMsg(t('common:done'));
            loadTab('balances');
        } catch (e) {
            setMsg(e.response?.data?.message || t('common:error'));
        }
    }

    const tabs = [
        { id: 'providers', label: t('adminSms:providers') },
        { id: 'tariffs', label: t('adminSms:tariffs') },
        { id: 'balances', label: t('adminSms:schoolsBalance') },
        { id: 'revenue', label: t('adminSms:revenue') }
    ];

    function renderTariffRow(tar, isNew = false) {
        const isEditing = isNew ? editingKey === 'new' : editingKey === tar.id;

        if (isEditing && editDraft) {
            return (
                <tr key={isNew ? 'new-tariff' : tar.id} className="sms-tariff-editing">
                    <td>
                        {isNew ? (
                            <TariffEditInput
                                value={editDraft.region}
                                onChange={(e) => patchDraft('region', e.target.value)}
                                style={{ minWidth: 72 }}
                            />
                        ) : (
                            tar.region
                        )}
                    </td>
                    <td>
                        <TariffEditInput
                            type="number"
                            value={editDraft.pricePerSms}
                            onChange={(e) => patchDraft('pricePerSms', e.target.value)}
                        />
                    </td>
                    <td>
                        <TariffEditInput
                            type="number"
                            value={editDraft.markupPercent}
                            onChange={(e) => patchDraft('markupPercent', e.target.value)}
                        />
                    </td>
                    <td>
                        <TariffEditInput
                            type="number"
                            value={editDraft.package100Price}
                            onChange={(e) => patchDraft('package100Price', e.target.value)}
                        />
                    </td>
                    <td>
                        <TariffEditInput
                            type="number"
                            value={editDraft.package500Price}
                            onChange={(e) => patchDraft('package500Price', e.target.value)}
                        />
                    </td>
                    <td>
                        <TariffEditInput
                            type="number"
                            value={editDraft.package1000Price}
                            onChange={(e) => patchDraft('package1000Price', e.target.value)}
                        />
                    </td>
                    <td>
                        <TariffEditInput
                            type="number"
                            value={editDraft.package5000Price}
                            onChange={(e) => patchDraft('package5000Price', e.target.value)}
                        />
                    </td>
                    <td>
                        <TariffEditInput
                            type="number"
                            value={editDraft.minBalanceToSend}
                            onChange={(e) => patchDraft('minBalanceToSend', e.target.value)}
                        />
                    </td>
                    <td>
                        <TariffEditInput
                            value={editDraft.currency}
                            onChange={(e) => patchDraft('currency', e.target.value)}
                            style={{ minWidth: 64 }}
                        />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                        <input
                            type="checkbox"
                            checked={!!editDraft.isActive}
                            onChange={(e) => patchDraft('isActive', e.target.checked)}
                        />
                    </td>
                    <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            <button
                                type="button"
                                className="sp-btn sp-btn--primary"
                                disabled={savingTariff}
                                onClick={submitTariff}
                                title="Сохранить"
                            >
                                💾
                            </button>
                            <button
                                type="button"
                                className="sp-btn sp-btn--ghost"
                                disabled={savingTariff}
                                onClick={cancelEditTariff}
                                title="Отмена"
                            >
                                ✕
                            </button>
                        </div>
                        <div style={{ marginTop: 6 }}>
                            <TariffEditInput
                                type="number"
                                value={editDraft.costPerSms}
                                onChange={(e) => patchDraft('costPerSms', e.target.value)}
                                style={{ minWidth: 72 }}
                            />
                            <span style={{ fontSize: 11, color: '#64748b' }}>{t('adminSms:costPrice')}</span>
                        </div>
                    </td>
                </tr>
            );
        }

        return (
            <tr key={tar.id}>
                <td>{tar.region}</td>
                <td>{fmt(tar.pricePerSms, tar.currency)}</td>
                <td>{tar.markupPercent}%</td>
                <td>{fmt(tar.package100Price, tar.currency)}</td>
                <td>{fmt(tar.package500Price, tar.currency)}</td>
                <td>{fmt(tar.package1000Price, tar.currency)}</td>
                <td>{fmt(tar.package5000Price, tar.currency)}</td>
                <td>{tar.minBalanceToSend}</td>
                <td>{tar.currency}</td>
                <td style={{ textAlign: 'center' }}>{tar.isActive ? '✅' : '—'}</td>
                <td>
                    <button
                        type="button"
                        className="sp-btn sp-btn--ghost"
                        disabled={editingKey != null}
                        onClick={() => startEditTariff(tar)}
                        title="Редактировать"
                    >
                        ✏️
                    </button>
                </td>
            </tr>
        );
    }

    return (
        <OwnerPageLayout title={t('adminSms:title')} maxWidth={1200}>
            <div className="sms-mgmt-tabs">
                {tabs.map((x) => (
                    <button
                        key={x.id}
                        type="button"
                        className={tab === x.id ? 'active' : ''}
                        onClick={() => setTab(x.id)}
                    >
                        {x.label}
                    </button>
                ))}
            </div>
            {msg ? <p className="sms-mgmt-msg">{msg}</p> : null}

            {tab === 'providers' && (
                <div className="sms-mgmt-panel">
                    <p className="sms-mgmt-hint">{t('adminSms:addProvider')} — через API POST /admin/sms/providers</p>
                    <input
                        className="sms-mgmt-input"
                        placeholder="+7..."
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                    />
                    <table className="sms-mgmt-table">
                        <thead>
                            <tr>
                                <th>{t('adminSms:providers')}</th>
                                <th>Code</th>
                                <th>Priority</th>
                                <th>{t('adminSms:costPrice')}</th>
                                <th>{t('adminSms:testProvider')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {providers.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.region} · {p.providerName} {p.isActive ? '✅' : '—'}</td>
                                    <td>{p.providerCode}</td>
                                    <td>{p.priority}{p.isDefault ? ' ★' : ''}</td>
                                    <td>{fmt(p.costPerSmsRub, 'RUB')}</td>
                                    <td>
                                        <button type="button" className="sp-btn sp-btn--ghost" onClick={() => testProvider(p.id)}>
                                            📤
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'tariffs' && (
                <div className="sms-mgmt-panel">
                    <div style={{ marginBottom: 12 }}>
                        <button
                            type="button"
                            className="sp-btn sp-btn--primary"
                            disabled={editingKey != null}
                            onClick={startNewTariff}
                        >
                            + Добавить тариф
                        </button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="sms-mgmt-table">
                            <thead>
                                <tr>
                                    <th>Регион</th>
                                    <th>{t('adminSms:sellPrice')}</th>
                                    <th>{t('adminSms:markupPercent')}%</th>
                                    <th>100</th>
                                    <th>500</th>
                                    <th>1000</th>
                                    <th>5000</th>
                                    <th>Мин. баланс</th>
                                    <th>Валюта</th>
                                    <th>Активен</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tariffs.map((tar) => renderTariffRow(tar))}
                                {editingKey === 'new' && editDraft ? renderTariffRow({}, true) : null}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'balances' && (
                <div className="sms-mgmt-panel">
                    <div className="sms-mgmt-adjust">
                        <input className="sms-mgmt-input" placeholder="schoolId" value={adjustSchoolId} onChange={(e) => setAdjustSchoolId(e.target.value)} />
                        <input className="sms-mgmt-input" placeholder="SMS ±" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} />
                        <input className="sms-mgmt-input" placeholder={t('adminSms:adjustBalance')} value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
                        <button type="button" className="sp-btn sp-btn--primary" onClick={adjustBalance}>+</button>
                    </div>
                    <table className="sms-mgmt-table">
                        <thead>
                            <tr>
                                <th>Школа</th>
                                <th>SMS</th>
                                <th>Куплено</th>
                                <th>Использовано</th>
                            </tr>
                        </thead>
                        <tbody>
                            {balances.map((b) => (
                                <tr key={b.schoolId} className={b.smsBalance <= 10 ? 'low' : ''}>
                                    <td>{b.schoolName}</td>
                                    <td>{b.smsBalance}{b.isBlocked ? ' 🔴' : ''}</td>
                                    <td>{b.totalPurchased}</td>
                                    <td>{b.totalUsed}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === 'revenue' && revenue && (
                <div className="sms-mgmt-panel">
                    <table className="sms-mgmt-table">
                        <thead>
                            <tr>
                                <th>Месяц</th>
                                <th>SMS</th>
                                <th>{t('adminSms:totalProfit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(revenue.usage || []).map((r) => (
                                <tr key={`${r.year}-${r.month}`}>
                                    <td>{r.month}.{r.year}</td>
                                    <td>{r.totalSms}</td>
                                    <td>{fmt(r.revenue, 'RUB')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </OwnerPageLayout>
    );
}
